/**
 * @pj/engine — the client-only presentation layer, built on PixiJS v8.
 *
 * It knows tiles, sprites, a camera, and input. It knows NOTHING about game
 * logic (collision verdicts, abilities, objectives) — it consumes protocol
 * Snapshots/Diffs and emits raw movement intents. That boundary is what keeps a
 * future authoritative server uncheatable: the renderer is "dumb".
 *
 * All art is generated at runtime (see ./art, ./tilemap): GBA-style 16px pixel
 * tiles + sprites at 1x, scaled 3x with nearest-neighbor sampling.
 */
import { Application, Container, Graphics, Sprite, Text } from "pixi.js";
import {
  TILE_SIZE,
  applyDiff,
  emptySnapshot,
  type Diff,
  type Dir,
  type EntitySnap,
  type WorldSnapshot,
} from "@pj/protocol";
import { getMap, type MapData } from "@pj/content";
import { getArtKit, type ArtKit } from "./art";
import { MapPainter } from "./tilemap";

export { getArtKit } from "./art";

const ZOOM = 3;
/** Gen-3 tile-animation cadence: one frame every 16 GBA frames (~267ms). */
const ANIM_MS = 267;
/** Surf-blob bob: 0→4→0 px over a 533ms period. */
const BOB_MS = 533;
const FADE_MS = 280;

const KEY_TO_DIR: Record<string, Dir> = {
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
  KeyW: "up",
  KeyS: "down",
  KeyA: "left",
  KeyD: "right",
};

export interface GameViewOptions {
  mapId: string;
  /** Called whenever the held movement direction changes (null = released). */
  onIntent: (dir: Dir | null) => void;
  /** Space / Enter / Z — the A button (interact / advance message). */
  onInteract?: (() => void) | undefined;
  /** Name shown on the banner above the player's head. */
  playerLabel?: string | undefined;
}

interface EntityView {
  container: Container;
  body: Sprite;
  blob: Sprite | null;
  kind: EntitySnap["kind"];
}

export class GameView {
  readonly app = new Application();
  private readonly world = new Container();
  private readonly entityLayer = new Container();
  private readonly fade = new Graphics();
  private readonly views = new Map<string, EntityView>();
  private readonly kit: ArtKit = getArtKit();
  private map: MapData;
  private painter: MapPainter | null = null;
  private ground: Sprite | null = null;
  private readonly onIntent: (dir: Dir | null) => void;
  private readonly onInteract: (() => void) | undefined;
  private readonly playerLabel: string;
  private snapshot: WorldSnapshot;
  private followId: string | null = null;
  private readonly pressed: Dir[] = [];
  private disposed = false;
  private animAcc = 0;
  private animFrame = 0;
  private timeMs = 0;
  private fadeAlpha = 0;
  private fading = false;
  private keydownHandler = (e: KeyboardEvent): void => this.onKeyDown(e);
  private keyupHandler = (e: KeyboardEvent): void => this.onKeyUp(e);

  constructor(opts: GameViewOptions) {
    this.map = getMap(opts.mapId);
    this.onIntent = opts.onIntent;
    this.onInteract = opts.onInteract;
    this.playerLabel = opts.playerLabel ?? "poopie";
    this.snapshot = emptySnapshot(opts.mapId);
  }

  async init(parent: HTMLElement): Promise<void> {
    await this.app.init({
      background: 0x101418,
      resizeTo: parent,
      antialias: false,
      roundPixels: true,
    });
    if (this.disposed) {
      this.app.destroy(true);
      return;
    }
    parent.appendChild(this.app.canvas);

    this.world.scale.set(ZOOM);
    this.entityLayer.sortableChildren = true;
    this.world.addChild(this.entityLayer);
    this.app.stage.addChild(this.world);
    this.fade.rect(0, 0, 8192, 8192).fill({ color: 0x000000 });
    this.fade.alpha = 0;
    this.app.stage.addChild(this.fade);

    this.buildGround();

    window.addEventListener("keydown", this.keydownHandler);
    window.addEventListener("keyup", this.keyupHandler);
    this.app.ticker.add(() => this.onTick());
  }

  setFollow(id: string): void {
    this.followId = id;
  }

  applyDiff(diff: Diff): void {
    const mapChanged = diff.mapId !== this.snapshot.mapId;
    this.snapshot = applyDiff(this.snapshot, diff);
    if (mapChanged) {
      // Warp: cut to black instantly, rebuild the world, fade back in.
      this.map = getMap(diff.mapId);
      this.fadeAlpha = 1;
      this.fading = true;
      this.buildGround();
    }
    for (const e of diff.upsert) this.upsertEntity(e);
    for (const id of diff.remove) this.removeEntity(id);
  }

  applySnapshot(snap: WorldSnapshot): void {
    this.snapshot = snap;
    for (const e of Object.values(snap.entities)) this.upsertEntity(e);
  }

  destroy(): void {
    this.disposed = true;
    window.removeEventListener("keydown", this.keydownHandler);
    window.removeEventListener("keyup", this.keyupHandler);
    this.painter?.destroy();
    if (this.app.renderer) this.app.destroy(true, { children: true });
  }

  private buildGround(): void {
    if (this.ground) {
      this.world.removeChild(this.ground);
      this.ground.destroy();
      this.painter?.destroy();
    }
    this.painter = new MapPainter(this.map);
    this.ground = new Sprite(this.painter.texture);
    this.world.addChildAt(this.ground, 0);
  }

  // --- entities -------------------------------------------------------------------

  private upsertEntity(e: EntitySnap): void {
    let view = this.views.get(e.id);
    if (!view) {
      const container = new Container();
      let body: Sprite;
      let blob: Sprite | null = null;
      if (e.kind === "player" || e.kind === "npc") {
        blob = new Sprite(this.kit.blob);
        blob.visible = false;
        container.addChild(blob);
        const actor = this.kit.actors[e.sprite] ?? this.kit.player;
        body = new Sprite(actor.walk.down[0]!);
        body.anchor.set(0.5, 1);
        body.x = TILE_SIZE / 2;
        body.y = TILE_SIZE;
        container.addChild(body);
        if (e.kind === "player") this.addLabel(container, this.playerLabel);
        else if (e.id === "aanya") this.addLabel(container, "aanya");
      } else {
        const tex = this.kit.objects[e.sprite] ?? this.kit.objects["berry"]!;
        body = new Sprite(tex);
        container.addChild(body);
      }
      this.entityLayer.addChild(container);
      view = { container, body, blob, kind: e.kind };
      this.views.set(e.id, view);
    }
    if (view.kind === "door") {
      const tex = this.kit.objects[e.sprite];
      if (tex) view.body.texture = tex;
    }
    view.container.x = e.px;
    view.container.y = e.py;
    // Doors render under everything; actors above items at the same y.
    view.container.zIndex =
      e.kind === "door" ? e.py - 8 : e.kind === "player" || e.kind === "npc" ? e.py + 1 : e.py;
  }

  private addLabel(container: Container, text: string): void {
    const label = new Text({
      text,
      style: {
        fontFamily: "monospace",
        fontSize: 5,
        fill: 0xffffff,
        stroke: { color: 0x101418, width: 2 },
        align: "center",
      },
    });
    label.anchor.set(0.5, 1);
    label.resolution = 4;
    label.x = TILE_SIZE / 2;
    label.y = -18;
    const pad = 2;
    const banner = new Graphics();
    banner
      .roundRect(-label.width / 2 - pad, -label.height - pad, label.width + pad * 2, label.height + pad, 2)
      .fill({ color: 0x101418, alpha: 0.78 });
    banner.x = TILE_SIZE / 2;
    banner.y = -18;
    container.addChild(banner);
    container.addChild(label);
  }

  private removeEntity(id: string): void {
    const view = this.views.get(id);
    if (!view) return;
    view.container.destroy({ children: true });
    this.views.delete(id);
  }

  // --- per-frame update ---------------------------------------------------------

  private onTick(): void {
    const dt = this.app.ticker.deltaMS;
    this.timeMs += dt;

    // Tile animation (water swell, flowers) on the Gen-3 cadence.
    this.animAcc += dt;
    while (this.animAcc >= ANIM_MS) {
      this.animAcc -= ANIM_MS;
      this.animFrame++;
      this.painter?.animate(this.animFrame);
    }

    // Fade-in after warps.
    if (this.fading) {
      this.fadeAlpha = Math.max(0, this.fadeAlpha - dt / FADE_MS);
      if (this.fadeAlpha === 0) this.fading = false;
    }
    this.fade.alpha = this.fadeAlpha;

    // Actor animation: walk cycle, surf blob + bob.
    const bobPhase = (this.timeMs % BOB_MS) / BOB_MS;
    const bob = Math.round(2 - 2 * Math.cos(bobPhase * Math.PI * 2));
    for (const [id, view] of this.views) {
      const snap = this.snapshot.entities[id];
      if (!snap) continue;
      if (view.kind === "player" || view.kind === "npc") {
        this.updateActor(view, snap, bob);
      } else if (snap.sprite === "pearl") {
        view.body.y = bob > 2 ? 1 : 0; // pearls float on the pond
      }
    }

    this.centerCamera();
  }

  private updateActor(view: EntityView, snap: EntitySnap, bob: number): void {
    const facing3 = snap.facing === "right" ? "left" : snap.facing;
    const actor = this.kit.actors[snap.sprite] ?? this.kit.player;
    const frames = actor.walk[facing3];
    let frame: number;
    if (snap.surfing || !snap.moving) {
      frame = 0;
    } else {
      // Quarter-tile walk cadence: stepA → stand → stepB → stand.
      const q = Math.floor((snap.px + snap.py) / 4) % 4;
      frame = q === 0 ? 1 : q === 2 ? 2 : 0;
    }
    view.body.texture = frames[frame]!;
    view.body.scale.x = snap.facing === "right" ? -1 : 1;
    if (view.blob) {
      view.blob.visible = snap.surfing;
      view.blob.y = snap.surfing ? bob - 2 : 0;
    }
    view.body.y = snap.surfing ? TILE_SIZE - 5 + (bob - 2) : TILE_SIZE;
  }

  private centerCamera(): void {
    const target = this.followId ? this.snapshot.entities[this.followId] : undefined;
    if (!target) return;
    const sw = this.app.screen.width;
    const sh = this.app.screen.height;
    const mw = this.map.width * TILE_SIZE * ZOOM;
    const mh = this.map.height * TILE_SIZE * ZOOM;
    const cx = (target.px + TILE_SIZE / 2) * ZOOM;
    const cy = (target.py + TILE_SIZE / 2) * ZOOM;
    const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);
    this.world.x = Math.round(mw <= sw ? (sw - mw) / 2 : clamp(sw / 2 - cx, sw - mw, 0));
    this.world.y = Math.round(mh <= sh ? (sh - mh) / 2 : clamp(sh / 2 - cy, sh - mh, 0));
  }

  // --- input ---------------------------------------------------------------------

  private current(): Dir | null {
    return this.pressed[this.pressed.length - 1] ?? null;
  }

  private onKeyDown(e: KeyboardEvent): void {
    if (e.code === "Space" || e.code === "Enter" || e.code === "KeyZ") {
      e.preventDefault();
      if (!e.repeat) this.onInteract?.();
      return;
    }
    const dir = KEY_TO_DIR[e.code];
    if (!dir) return;
    e.preventDefault();
    if (e.repeat) return;
    const before = this.current();
    if (!this.pressed.includes(dir)) this.pressed.push(dir);
    const after = this.current();
    if (after !== before) this.onIntent(after);
  }

  private onKeyUp(e: KeyboardEvent): void {
    const dir = KEY_TO_DIR[e.code];
    if (!dir) return;
    const before = this.current();
    const i = this.pressed.indexOf(dir);
    if (i >= 0) this.pressed.splice(i, 1);
    const after = this.current();
    if (after !== before) this.onIntent(after);
  }
}
